import { useQuery, type UseQueryResult } from '@tanstack/react-query';

export interface VolumeTier {
  min: number;
  max?: number;
  percent: number;
}

export interface PricingData {
  prices: Record<string, { gbp: number }>;
  finishes?: string[];
  volume_discounts?: VolumeTier[];
}

/** Error message thrown for a 503 (pricing temporarily unavailable). */
export const PRICING_UNAVAILABLE = 'PRICING_UNAVAILABLE';

async function fetchPricing(): Promise<PricingData> {
  const res = await fetch('/api/v1/print/pricing', { credentials: 'include' });
  if (res.status === 503) throw new Error(PRICING_UNAVAILABLE);
  if (!res.ok) throw new Error(`Failed to fetch pricing (${res.status})`);
  return res.json();
}

/**
 * Shared print pricing query. The cache is keyed globally so the gallery's
 * running total and the checkout page reuse a single fetch. Pass `enabled`
 * to defer the request until print pricing is actually needed.
 */
export function usePrintPricing(enabled = true): UseQueryResult<PricingData> {
  return useQuery<PricingData>({
    queryKey: ['print-pricing'],
    queryFn: fetchPricing,
    staleTime: 1000 * 60 * 5,
    retry: 1,
    enabled,
  });
}

/** True when the query failed because pricing is temporarily unavailable (503). */
export function isPricingUnavailable(error: unknown): boolean {
  return error instanceof Error && error.message === PRICING_UNAVAILABLE;
}

/** Volume discount % for a given total order quantity across all sizes. */
export function discountPercent(totalQty: number, tiers: VolumeTier[] | undefined): number {
  if (!tiers) return 0;
  const tier = tiers.find(
    (t) => totalQty >= t.min && (t.max == null || totalQty <= t.max),
  );
  return tier ? tier.percent : 0;
}

export function gbp(amount: number): string {
  return `£${amount.toFixed(2)}`;
}

/** Discounted unit price for a size, or null if the base price is unknown. */
export function unitPrice(
  size: string,
  pricing: PricingData | undefined,
  pct: number,
): number | null {
  const base = pricing?.prices?.[size]?.gbp;
  if (base == null) return null;
  return base * (1 - pct / 100);
}

/** Estimated order total across all lines, or null if any price is unknown. */
export function estimatedTotal(
  lines: Array<{ size: string; quantity: number }>,
  pricing: PricingData | undefined,
  pct: number,
): number | null {
  if (!pricing?.prices) return null;
  let total = 0;
  for (const l of lines) {
    const unit = unitPrice(l.size, pricing, pct);
    if (unit == null) return null;
    total += unit * l.quantity;
  }
  return total;
}

/** Human-readable print-count range for a volume tier (e.g. `10–19`, `50+`). */
export function tierRangeLabel(tier: VolumeTier): string {
  if (tier.max == null) return `${tier.min}+`;
  if (tier.max === tier.min) return `${tier.min}`;
  return `${tier.min}–${tier.max}`;
}
